import React from 'react';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import { usertype_permissions } from '../../enums/PermissionEnums';

// Import Zustand store
import { useAppStore } from '../../stores';

interface Props {
  attendeeRecord?: unknown;
  isTablet?: boolean;
  showRepAffiliate?: boolean;
  user: {
    firstName?: string;
    lastName?: string;
    memberId?: string;
    address1?: string;
    city?: string;
    stateCode?: string;
    zip?: string;
    contactEmail?: string;
    ccEmail?: string;
    phone?: string;
    affiliate?: {
      affiliateNumber?: string;
      affiliateName?: string;
    };
    badgeNumber?: number;
    credentialType?: string;
    confirmationNumber?: string;
    inviteeStatus?: string;
    credentialTypeId?: string;
    committeeChoice1?: number;
    committeeChoice2?: number;
    committeeChoice3?: number;
    repAffiliateId?: number;
    repAffiliateGuid?: string;
    homeAffiliateGuid?: string;
    parentAffiliate?: {
      affiliateNumber?: string;
      affiliateName?: string;
    };
    createdDate?: string;
    modifiedDate?: string;
  };
}

interface Permission {
  name: string;
}

interface CommitteeType {
  committeeTypeId: number;
  committeeTypeName?: string;
}

const UserInformation: React.FC<Props> = (Props: Props) => {
  // Zustand store
  const permissions = Object.values(useAppStore((state) => state.user.permissions)) as Permission[];
  const committeeTypes = useAppStore(
    (state) => state.committees.committeeTypes
  ) as CommitteeType[];

  const isAdmin =
    _.find(permissions, (permission: Permission) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1
      );
    }) !== undefined;

  const canViewDate =
    _.find(permissions, (permission: Permission) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1
      );
    }) !== undefined;

  const noDataPlaceholder = '---';

  const formatCreatedDate = () => {
    if (Props.user.createdDate == null) return '';
    const d = new Date(Props.user.createdDate);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatModifiedDate = () => {
    if (Props.user.modifiedDate == null) return '';
    const d = new Date(Props.user.modifiedDate);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isGuest = () => {
    return Props.user.credentialTypeId === '13';
  };

  return (
    <Grid container>
      {!Props.isTablet && (
        <Grid size={{ xs: 12, sm: 12, md: 12 }} className="user-info-title">
          Attendee Details
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <Grid className="user-info-head">
          <span>
            {(Props.user.firstName && Props.user.firstName) +
              ' ' +
              (Props.user.lastName && Props.user.lastName)}
          </span>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <div className="user-info-body">
            <Grid container className="mb-1">
              {/* Member ID */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                <span className="user-info-label">Member ID:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                <p>{Props.user.memberId || noDataPlaceholder}</p>
              </Grid>

              {/* Mailing Address */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                <span className="user-info-label">Mailing Address:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                <p>
                  {Props.user.address1 ? (
                    <>
                      {Props.user.address1}
                      <br />
                      {`${Props.user.city || ''}${Props.user.city ? ',' : ''} ${
                        Props.user.stateCode || ''
                      } ${Props.user.zip || ''}`}
                    </>
                  ) : noDataPlaceholder}
                </p>
              </Grid>

              {/* Email */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                <span className="user-info-label">Email:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <p>{Props.user.contactEmail || noDataPlaceholder}</p>
              </Grid>

              {/* CC Email */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mb: 0 } }}>
                <span className="user-info-label">CC Email:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' }, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <p>{Props.user.ccEmail || noDataPlaceholder}</p>
              </Grid>

              {/* Phone */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                <span className="user-info-label">Phone:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                <p>
                  {Props.user.phone
                    ? Props.user.phone
                        .replace(/\D+/g, '')
                        .replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
                    : noDataPlaceholder}
                </p>
              </Grid>

              {/* Affiliate Number */}
              {!isGuest() && (
                <>
                  <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mb: 0 } }}>
                    <span className="user-info-label">Affiliate Number:</span>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                    <p>
                      {Props.user.affiliate
                        ? Props.user.affiliate.affiliateNumber
                        : noDataPlaceholder}
                    </p>
                  </Grid>
                </>
              )}

              {/* Badge Number */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                <span className="user-info-label">Badge Number:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                <p>
                  {Props.user.badgeNumber
                    ? Props.user.badgeNumber.toString().padStart(4, '0')
                    : noDataPlaceholder}
                </p>
              </Grid>

              {/* Status */}
              {!isGuest() && (
                <>
                  <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                    <span className="user-info-label">Status:</span>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                    <p>Active</p>
                  </Grid>
                </>
              )}

              {/* Type */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                <span className="user-info-label">Type:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                <p>
                  {!!Props.user.credentialType
                    ? Props.user.credentialType
                    : 'Not Registered'}
                </p>
              </Grid>

              {/* Confirmation Number */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                <span className="user-info-label">Confirmation Number:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                <p>
                  {!!Props.user.confirmationNumber
                    ? Props.user.confirmationNumber
                    : 'Not Registered'}
                </p>
              </Grid>

              {/* Invitee Status */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }} sx={{ '& p': { mt: 0 } }}>
                <span className="user-info-label">Invitee Status:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right', '& p': { mb: '4px' } }}>
                <p>
                  {!!Props.user.inviteeStatus
                    ? Props.user.inviteeStatus
                    : 'Not Registered'}
                </p>
              </Grid>

            </Grid>
          </div>
        </Grid>

        <div className="user-info-footer">
          {!isGuest() && (
            <Grid container className="mb-1">
              {/* Home Affiliate */}
              <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                <span className="user-info-label">Home Affiliate:</span>
              </Grid>
              <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                <p>
                  {Props.user.affiliate
                    ? Props.user.affiliate.affiliateNumber +
                      ' - ' +
                      Props.user.affiliate.affiliateName
                    : noDataPlaceholder}
                </p>
              </Grid>

              {/* Represented Affiliate */}
              {Props.showRepAffiliate && (
                <>
                  <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                    <span className="user-info-label">Rep Local:</span>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                    {Props.user.repAffiliateGuid ==
                    Props.user.homeAffiliateGuid ? (
                      <p>
                        {Props.user.affiliate
                          ? Props.user.affiliate.affiliateNumber +
                            ' - ' +
                            Props.user.affiliate.affiliateName
                          : noDataPlaceholder}
                      </p>
                    ) : (
                      <p>
                        {Props.user.parentAffiliate
                          ? Props.user.parentAffiliate.affiliateNumber +
                            ' - ' +
                            Props.user.parentAffiliate.affiliateName
                          : noDataPlaceholder}
                      </p>
                    )}
                  </Grid>
                </>
              )}

              {/* Date Created */}
              {canViewDate && (
                <>
                  <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                    <span className="user-info-label">Date Created:</span>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                    <p>{formatCreatedDate() || noDataPlaceholder}</p>
                  </Grid>
                </>
              )}

              {/* Date Modified */}
              {canViewDate && (
                <>
                  <Grid size={{ xs: 5, sm: 5, md: 5 }}>
                    <span className="user-info-label">Date Modified:</span>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 7, md: 7 }} sx={{ textAlign: 'right' }}>
                    <p>{formatModifiedDate() || noDataPlaceholder}</p>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default UserInformation;
